import { Typography, Box, CircularProgress, TextField } from "@mui/material";
import { Email, CheckCircle } from "@mui/icons-material";
import { useForm, SubmitHandler } from "react-hook-form";
import { useRequestEmailVerificationMutation } from "features/auth/auth";
import CustomButton from "./common/CustomButton";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

type SetEmailInputs = {
  email: string;
};

export default function SetEmailPrompt() {
  const [requestVerification, { isLoading }] = useRequestEmailVerificationMutation();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SetEmailInputs>();

  const onSubmit: SubmitHandler<SetEmailInputs> = async (data) => {
    setServerError(null);

    try {
      await requestVerification({ email: data.email }).unwrap();
      setSubmittedEmail(data.email);
      setEmailSent(true);
    } catch (error: any) {
      if (error?.data?.status === "error" && error?.data?.error?.email) {
        setServerError(error.data.error.email[0] || error.data.error.email);
      } else {
        setServerError(error?.data?.message || "Something went wrong");
      }
    }
  };

  const handleSkip = () => {
    navigate("/");
  };

  if (emailSent) {
    return (
      <Box
        marginTop={12}
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          width: "100%",
          maxWidth: 400,
          boxSizing: "border-box",
          px: { xs: 2, sm: 3 },
          bgcolor: "#fff",
          p: { xs: 2, sm: 4 },
          borderRadius: 1.5,
          border: "1px solid #e0e0e0",
          mx: "auto",
        }}
      >
        <CheckCircle sx={{ fontSize: 64, color: "#4caf50", mb: 2 }} />

        <Typography
          component="h1"
          variant="h5"
          sx={{ color: "rgb(31, 93, 107)", mb: 2, textAlign: "center" }}
        >
          Check Your Email
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center" }}
        >
          We sent a verification link to <strong>{submittedEmail}</strong>.
          Click the link in the email to verify your address.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      marginTop={12}
      display="flex"
      flexDirection="column"
      alignItems="center"
      sx={{
        width: "100%",
        maxWidth: 400,
        boxSizing: "border-box",
        px: { xs: 2, sm: 3 },
        bgcolor: "#fff",
        p: { xs: 2, sm: 4 },
        borderRadius: 1.5,
        border: "1px solid #e0e0e0",
        mx: "auto",
      }}
    >
      <Typography
        component="h1"
        variant="h4"
        sx={{ color: "rgb(31, 93, 107)", mb: 2 }}
      >
        Add Your Email
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, textAlign: "center" }}
      >
        Adding an email allows you to recover your password if you forget it.
        This step is optional.
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{ width: "100%" }}
      >
        <TextField
          fullWidth
          label="Email Address"
          id="email"
          type="email"
          autoComplete="email"
          autoFocus
          error={Boolean(errors.email) || Boolean(serverError)}
          helperText={errors.email?.message || serverError || ""}
          InputProps={{
            startAdornment: <Email sx={{ color: "gray", mr: 1 }} />,
          }}
          sx={{ mb: 3 }}
          {...register("email", {
            required: "Email is required",
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          })}
        />

        {isLoading ? (
          <Box display="flex" justifyContent="center">
            <CircularProgress />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={1.5}>
            <CustomButton type="submit">Add Email</CustomButton>
            <CustomButton
              type="button"
              onClick={handleSkip}
              sx={{
                bgcolor: "transparent",
                color: "text.secondary",
                borderColor: "#ccc",
                "&:hover": {
                  bgcolor: "#f5f5f5",
                  color: "text.primary",
                  borderColor: "#999",
                },
              }}
            >
              Skip for Now
            </CustomButton>
          </Box>
        )}
      </Box>
    </Box>
  );
}
